import * as Sentry from '@sentry/react'

import { env } from '@/shared/config/env'

type Extra = Record<string, unknown>

class Logger {
	/**
	 * Log information
	 *
	 * @param category A log message category that will be prepended
	 * @param extra Arbitrary data to be logged that will appear in prod logs
	 */
	info(label: string, message: string, extra?: Extra) {
		console.info(`[${label}] ${message}`, extra)
	}

	/**
	 * Debug information
	 *
	 * @param category A log message category that will be prepended
	 * @param extra Arbitrary data to be logged
	 */
	debug(label: string, message: string, extra?: Extra) {
		if (env.NODE_ENV !== 'production') {
			console.info(`[${label}] ${message}`, extra)
		}
	}

	/**
	 * Log a warning
	 *
	 * @param message A warning message
	 * @param extra Arbitrary data to be logged that will appear in prod logs
	 */
	warn(message: string, extra?: Extra) {
		if (env.VITE_SENTRY_DSN) {
			Sentry.withScope(function (scope) {
				scope.setLevel('warning')

				for (const key in extra) {
					scope.setExtra(key, extra[key])
				}

				Sentry.captureMessage(message)
			})
		}

		console.warn(message, extra)
	}

	/**
	 * Report a runtime error
	 *
	 * @param message A description of the error
	 * @param error The error that occurred
	 * @param extra Arbitrary data to be logged that will appear in prod logs
	 */
	error(message: string, error: Error, extra?: Extra) {
		if (env.VITE_SENTRY_DSN) {
			Sentry.withScope(function (scope) {
				scope.setLevel('error')

				for (const key in extra) {
					scope.setExtra(key, extra[key])
				}

				Sentry.captureException(error)
			})
		}

		console.error(message, {
			error,
			extra,
		})
	}
}

export default new Logger()
